import React from "react";

const PhoneMockup = () => {
  return (
    <div className="relative flex justify-center min-h-[420px] lg:min-h-[520px]">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-10 -left-10 h-44 w-44 rounded-full bg-blue-300/50" />
      <div className="pointer-events-none absolute -bottom-10 -right-10 h-36 w-36 rounded-full bg-emerald-200/50" />

      {/* Reserve the final (scaled) size in layout */}
      <div className="relative w-[200px] lg:w-[250px]">
        {/* Scale only this inner wrapper (doesn't affect layout anymore) */}
        <div className="lg:scale-125 lg:origin-top">
          {/* Phone */}
          <div className="relative w-[200px]">
            {/* Outer frame - thin black */}
            <div className="relative rounded-[2rem] bg-black p-[3px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.4)]">
              {/* Side buttons */}
              <div className="pointer-events-none absolute left-[-2px] top-16 h-5 w-[2px] rounded-l-sm bg-slate-700" />
              <div className="pointer-events-none absolute left-[-2px] top-24 h-8 w-[2px] rounded-l-sm bg-slate-700" />
              <div className="pointer-events-none absolute right-[-2px] top-20 h-10 w-[2px] rounded-r-sm bg-slate-700" />

              {/* Screen */}
              <div className="relative overflow-hidden rounded-[1.85rem] bg-white">
                {/* Dynamic Island */}
                <div className="absolute left-1/2 top-1.5 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-black" />

                {/* Status bar */}
                <div className="relative z-10 flex items-center justify-between px-5 pt-2 text-[9px] font-medium text-slate-800">
                  <span>9:41</span>
                  <div className="flex items-center gap-1">
                    <svg
                      className="h-2.5 w-2.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
                    </svg>
                    <div className="relative h-2 w-4 rounded-[2px] border border-slate-800">
                      <div className="absolute inset-[1px] right-[2px] rounded-[1px] bg-slate-800" />
                    </div>
                  </div>
                </div>

                {/* URL bar */}
                <div className="relative z-10 mx-2 mt-1.5">
                  <div className="flex items-center justify-center gap-1 rounded-md bg-slate-100 py-1 text-[8px]">
                    <svg
                      className="h-2 w-2 text-slate-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-medium text-slate-700">
                      lloydfunding.com
                    </span>
                  </div>
                </div>

                {/* Header */}
                <div className="relative z-10 mt-2 flex items-center justify-between px-2">
                  <div className="flex items-center gap-1">
                    <svg
                      className="h-3 w-auto"
                      viewBox="-5 -5 160 130"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g className="fill-blue-600">
                        <path d="M57,36.1l-22.6,41.8c-0.3,0.5,0.1,1.2,0.7,1.2h47.8l19,34.6H29.8c-23.9,0-39.2-25.3-28.2-46.5l16.1-31H57z" />
                        <path d="M84.6,78.1l22.6-41.8c0.3-0.5-0.1-1.2-0.7-1.2H58.7l-19-34.6h72.1c23.9,0,39.2,25.3,28.2,46.5l-16.1,31H84.6z" />
                      </g>
                    </svg>
                    <span className="text-[8px] font-bold text-blue-900 tracking-tight">
                      Lloyd Funding
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="rounded-md bg-blue-600 px-2 py-0.5 text-[7px] font-semibold text-white">
                      Apply Now
                    </div>
                    <div className="grid h-5 w-5 place-items-center rounded-md bg-slate-100">
                      <div className="space-y-[2px]">
                        <div className="h-[1.5px] w-2.5 bg-slate-600" />
                        <div className="h-[1.5px] w-2.5 bg-slate-600" />
                        <div className="h-[1.5px] w-2.5 bg-slate-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Application Form */}
                <div className="relative z-10 px-2 pb-3 pt-2">
                  <div className="rounded-lg bg-white p-2 shadow-lg ring-1 ring-slate-100">
                    {[
                      "First Name",
                      "Last Name",
                      "Email Address",
                      "Phone Number",
                    ].map((label) => (
                      <div key={label} className="mb-1.5">
                        <div className="text-[7px] font-medium text-slate-600">
                          {label}
                        </div>
                        <div className="mt-0.5 h-5 rounded-md border border-slate-200 bg-slate-50" />
                      </div>
                    ))}

                    <div className="mb-1.5">
                      <div className="text-[7px] font-medium text-slate-600">
                        Monthly Revenue
                      </div>
                      <div className="mt-0.5 flex h-5 items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[7px] text-slate-400">
                        <span>Select...</span>
                        <span>▾</span>
                      </div>
                    </div>

                    <div className="mb-2">
                      <div className="text-[7px] font-medium text-slate-600">
                        Amount Requested
                      </div>
                      <div className="mt-0.5 flex h-5 items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[7px] text-slate-400">
                        <span>Select...</span>
                        <span>▾</span>
                      </div>
                    </div>

                    <div className="rounded-md bg-blue-600 py-1 text-center text-[8px] font-semibold text-white shadow-sm">
                      Submit Application
                    </div>

                    <p className="mt-1.5 text-center text-[6px] leading-snug text-slate-400">
                      By submitting, you agree to be contacted about funding
                      options.
                    </p>
                  </div>
                </div>

                {/* Home indicator */}
                <div className="flex justify-center pb-1.5">
                  <div className="h-1 w-16 rounded-full bg-slate-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;
